// -----------------------------------------------------------------------------
// LogCallPanel  (Wave A)
//
// Action panel body for PHONE_CALLED / LOG_PHONE_CALL.
// Fields: outcome (answered / no-answer / voicemail), duration (minutes),
//         notes (textarea).
// Submit: POST a note with contacted_via='PHONE', then call onComplete.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    Stack,
    IconButton,
    Tooltip,
} from '@mui/material';
import { dispatch, useSelector } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { getCallScriptSection } from 'src/redux/slices/callScript';
import { callApi } from 'src/utils/apiCall';
import { normalizePracticeId } from 'src/utils/contactPracticeContext';
import { CONTACT_MADE_NOTE, CONTACT_ATTEMPTED_NOTE } from 'src/constants/appointmentReason';
import Iconify from 'src/components/Iconify';
import CallScriptContainer from 'src/components/fullScreenPopUps/CallScriptContainer';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import useAuth from 'src/hooks/useAuth';
import TwilioCallActionButton from 'src/sections/twilioVoice/TwilioCallActionButton';

const OUTCOMES = [
    { value: 'answered',  label: 'Answered' },
    { value: 'no-answer', label: 'No answer' },
    { value: 'voicemail', label: 'Voicemail' },
];

const OUTCOME_TO_ENUM = { answered: 'ANSWERED', 'no-answer': 'NO_ANSWER', voicemail: 'VOICEMAIL' };

// Outcome -> note type. The type is what makes the logged call visible to the
// contacted count and the Last Contact column; see CONTACT_MADE_NOTE.
// A voicemail is an attempt, not a conversation.
const OUTCOME_TO_NOTE_TYPE = {
    answered: CONTACT_MADE_NOTE,
    'no-answer': CONTACT_ATTEMPTED_NOTE,
    voicemail: CONTACT_ATTEMPTED_NOTE,
};
const MISSING_PRACTICE_MESSAGE = 'Practice context is unavailable. Close this window and try again.';

export default function LogCallPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
    callScriptCategory,
    onBack,
    backLabel,
    onOpenSidebarTab,
}) {
    const [outcome, setOutcome] = useState('answered');
    const [duration, setDuration] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { user } = useAuth();
    const leadPhone = leadDetail?.lead_contact?.phone || leadDetail?.phone || leadDetail?.phone_number || '';
    const resolvedPracticeId = normalizePracticeId(practiceId);

    // WhatsApp call is feature-gated per practice — SAME gate as the sidebar call
    // button (WhatsappCallControls): superuser OR the 'whatsapp-calling' feature.
    // When enabled, the CTA opens the WhatsApp sidebar (call controls live there);
    // when off the tooltip reads "coming soon".
    const waCallEnabled = Boolean(user?.is_superuser) || hasFeatureAccess('whatsapp-calling', resolvedPracticeId);

    // Call-script viewer — open the practice's script for this call context
    // (TCO for the attended popup, OUTBOUND enquiry for the follow-up popup).
    // The category is supplied by the host popup via the ActionPanel.
    const [scriptOpen, setScriptOpen] = useState(false);
    const [scriptLoading, setScriptLoading] = useState(false);
    const { callScriptData } = useSelector((state) => state.callScript);
    const isTcoScript = callScriptCategory === 'TCO';

    const handleOpenScript = () => {
        setScriptLoading(true);
        const treatmentName =
            leadDetail?.lead_treatments?.[0]?.treatment?.name
            || leadDetail?.lead_treatments?.[0]?.treatment_name
            || leadDetail?.treatment_name
            || '';
        dispatch(getCallScriptSection({
            practiceId: resolvedPracticeId,
            treatment: treatmentName,
            isTemplate: false,
            category: callScriptCategory,
        }));
        setScriptOpen(true);
        setScriptLoading(false);
    };

    const handleSubmit = async () => {
        if (!resolvedPracticeId) {
            dispatch(updateError({ success: false, message: MISSING_PRACTICE_MESSAGE }));
            return;
        }
        setSubmitting(true);
        try {
            const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
            const lines = [
                `Call outcome: ${outcome}`,
                duration ? `Duration: ${duration} min` : '',
                notes,
            ].filter(Boolean);
            const additionalNotes = lines.join('\n');

            const url = `/api/v1/practices/${resolvedPracticeId}/leads/${leadId}/notes/`;
            const body = {
                lead: leadId,
                // LeadNote API stores free text in `additional_notes`; `description` is
                // ignored by the serializer (was silently dropping the call notes).
                additional_notes: additionalNotes,
                note: OUTCOME_TO_NOTE_TYPE[outcome] || CONTACT_ATTEMPTED_NOTE,
                contacted_via: 'PHONE',
                call_outcome: OUTCOME_TO_ENUM[outcome] || null,
                ...(treatmentId ? { treatment: treatmentId } : {}),
            };
            let res = await callApi(url, 'post', body);
            // Defensive: if a backend without the field rejects it (400 naming call_outcome),
            // retry without it so logging never breaks.
            if (res?.status === 400 && res?.data?.call_outcome) {
                const withoutOutcome = Object.fromEntries(
                    Object.entries(body).filter(([k]) => k !== 'call_outcome')
                );
                res = await callApi(url, 'post', withoutOutcome);
            }

            if (res.status === 201) {
                dispatch(updateError({ success: true, message: 'Call logged' }));
                onComplete?.(item);
            } else {
                const msg = res?.data?.detail || res?.data?.additional_notes?.[0] || 'Failed to log call';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="log-call-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(0, 167, 111, 0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:phone" width={18} height={18} sx={{ color: '#00A76F' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                        Log call
                    </Typography>
                    {leadDetail?.first_name && (
                        <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                            {[leadDetail.first_name, leadDetail.last_name].filter(Boolean).join(' ')}
                        </Typography>
                    )}
                </Box>

                <Stack direction="row" spacing={0.75} sx={{ ml: 'auto', flexShrink: 0 }}>
                    <TwilioCallActionButton
                        to={leadPhone}
                        leadId={leadId}
                        lead={leadDetail}
                        disabled={!leadPhone}
                        tooltip="Call lead"
                        onCallStart={() => onOpenSidebarTab?.('calls')}
                    />
                    <Tooltip title={waCallEnabled ? 'Start WhatsApp call' : 'WhatsApp call · coming soon'} arrow>
                        <Box component="span" sx={{ flexShrink: 0 }}>
                            <IconButton
                                data-testid="log-call-whatsapp-call"
                                onClick={() => { if (waCallEnabled) onOpenSidebarTab?.('whatsapp'); }}
                                disabled={!waCallEnabled || !onOpenSidebarTab}
                                aria-label="Start WhatsApp call"
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    border: '1px solid',
                                    color: waCallEnabled ? '#25D366' : '#919EAB',
                                    bgcolor: waCallEnabled ? 'rgba(37,211,102,0.10)' : 'transparent',
                                    borderColor: waCallEnabled ? 'rgba(37,211,102,0.32)' : 'rgba(145,158,171,0.28)',
                                    '&:hover': waCallEnabled ? { bgcolor: 'rgba(37,211,102,0.16)' } : {},
                                    '&.Mui-disabled': { color: '#919EAB', borderColor: 'rgba(145,158,171,0.24)' },
                                    '@keyframes waCallPulse': {
                                        '0%': { boxShadow: '0 0 0 0 rgba(37,211,102,0.45)' },
                                        '70%': { boxShadow: '0 0 0 7px rgba(37,211,102,0)' },
                                        '100%': { boxShadow: '0 0 0 0 rgba(37,211,102,0)' },
                                    },
                                    animation: waCallEnabled ? 'waCallPulse 1.8s ease-out infinite' : 'none',
                                    '& svg': {
                                        animation: waCallEnabled ? 'waCallWiggle 1.8s ease-in-out infinite' : 'none',
                                        transformOrigin: 'center',
                                    },
                                    '@keyframes waCallWiggle': {
                                        '0%, 60%, 100%': { transform: 'rotate(0deg)' },
                                        '70%': { transform: 'rotate(-12deg)' },
                                        '80%': { transform: 'rotate(12deg)' },
                                        '90%': { transform: 'rotate(-8deg)' },
                                    },
                                    '@media (prefers-reduced-motion: reduce)': {
                                        animation: 'none',
                                        '& svg': { animation: 'none' },
                                    },
                                }}
                            >
                                <Iconify icon="solar:phone-calling-rounded-outline" width={18} height={18} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Stack>
            </Box>

            {!resolvedPracticeId && (
                <Typography
                    data-testid="log-call-practice-error"
                    role="alert"
                    sx={{
                        px: 1.25,
                        py: 1,
                        borderRadius: '8px',
                        bgcolor: 'rgba(255, 86, 48, 0.08)',
                        color: 'error.dark',
                        fontSize: '12px',
                    }}
                >
                    {MISSING_PRACTICE_MESSAGE}
                </Typography>
            )}

            {/* Outcome + Duration — inline on one row (Duration is compact, so it
                sits beside the outcome picker rather than stacked below it). */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Outcome picker */}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                        Call outcome
                    </Typography>
                    <RadioGroup
                        data-testid="log-call-outcome"
                        row
                        value={outcome}
                        onChange={(e) => setOutcome(e.target.value)}
                    >
                        {OUTCOMES.map(({ value, label }) => (
                            <FormControlLabel
                                key={value}
                                value={value}
                                control={<Radio size="small" sx={{ color: '#6e6e73', '&.Mui-checked': { color: '#078DEE' } }} />}
                                label={<Typography sx={{ fontSize: '13px', color: '#1d1d1f' }}>{label}</Typography>}
                            />
                        ))}
                    </RadioGroup>
                </Box>

                {/* Duration */}
                <Box sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                        Duration (minutes)
                    </Typography>
                    <TextField
                        data-testid="log-call-duration"
                        type="number"
                        size="small"
                        placeholder="e.g. 5"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        inputProps={{ min: 0, max: 999 }}
                        sx={{ width: 120 }}
                    />
                </Box>
            </Box>

            {/* Notes */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Notes
                </Typography>
                <TextField
                    data-testid="log-call-notes"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    placeholder="What was discussed?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{
                        '& .MuiInputBase-root': { alignItems: 'flex-start' },
                        '& textarea': { resize: 'vertical', minHeight: '40px' },
                    }}
                />
            </Box>

            {/* Actions — back-to-channels sits to the LEFT of the primary Log call
                button (consistent with the other panels' back placement). */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                {onBack && (
                    <Button
                        data-testid="log-call-back"
                        variant="outlined"
                        size="small"
                        onClick={() => onBack()}
                        disabled={submitting}
                        startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={14} height={14} />}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#078DEE',
                            borderColor: 'rgba(7,141,238,0.4)',
                            '&:hover': { borderColor: '#078DEE', bgcolor: 'rgba(7,141,238,0.06)' },
                        }}
                    >
                        {backLabel || 'Back'}
                    </Button>
                )}
                <Button
                    data-testid="log-call-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={submitting || !resolvedPracticeId}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:phone" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#00A76F',
                        '&:hover': { bgcolor: '#2db14e' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Logging…' : 'Log call'}
                </Button>
                <Button
                    data-testid="log-call-view-script"
                    variant="outlined"
                    onClick={handleOpenScript}
                    disabled={scriptLoading || submitting || !resolvedPracticeId}
                    startIcon={<Iconify icon="eva:file-text-outline" width={16} height={16} />}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        flexShrink: 0,
                        color: '#078DEE',
                        borderColor: 'rgba(7,141,238,0.4)',
                        bgcolor: 'rgba(7,141,238,0.04)',
                        '&:hover': { borderColor: '#078DEE', bgcolor: 'rgba(7,141,238,0.08)' },
                    }}
                >
                    {isTcoScript ? 'TCO script' : 'Call script'}
                </Button>
                {!onBack && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onClose?.()}
                        disabled={submitting}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                    >
                        Cancel
                    </Button>
                )}
            </Stack>

            {/* Call-script viewer overlay. Outcome buttons inside the script just
                close it (the user returns here to record the call outcome). */}
            {scriptOpen && (
                <CallScriptContainer
                    open={scriptOpen}
                    onClose={() => setScriptOpen(false)}
                    scriptList={callScriptData?.results}
                    followLead={leadDetail}
                    setLeadId={() => {}}
                    setTreatmentId={() => {}}
                    onOpenAppointmentNotBooked={() => setScriptOpen(false)}
                    onOpenAppointmentBooked={() => setScriptOpen(false)}
                    onOpenUnableToContact={() => setScriptOpen(false)}
                    useV2
                    callType={isTcoScript ? 'inbound' : 'outbound'}
                    scriptCategory={callScriptCategory}
                    {...(isTcoScript ? { type: 'TCO' } : {})}
                />
            )}
        </Box>
    );
}

LogCallPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    /** Script category for the "View call script" button: 'TCO' | 'OUTBOUND' | 'INBOUND'. */
    callScriptCategory: PropTypes.oneOf(['TCO', 'OUTBOUND', 'INBOUND']),
    /** When provided, renders a back button to the LEFT of "Log call" (replaces the
     *  separate Cancel) — used by FollowUpPanel to return to the channel picker. */
    onBack: PropTypes.func,
    /** Label for the back button (e.g. "Back to channels" / "Close"). */
    backLabel: PropTypes.string,
    /** Called with 'calls' or 'whatsapp' to open the matching sidebar tab. */
    onOpenSidebarTab: PropTypes.func,
};

LogCallPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
    callScriptCategory: 'OUTBOUND',
    onBack: null,
    backLabel: 'Back',
    onOpenSidebarTab: null,
};
